import {
    Alert,
    AlertIcon,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Text,
    VStack,
} from '@chakra-ui/react';
import type { ElementType } from 'react';
import { useEffect, useState } from 'react';
import PartBayButton from '../../../../components/PartBayButton';
import { SettingsChoiceCards } from '../../../../components/settings/primitives';
import {
    ALL_POSITIONS,
    PartTypesPicker,
    RuleInsurancePicker,
    RuleMakesPicker,
} from './PartAllowanceCells';
import {
    defaultPartTypes,
    draftToAllowable,
    type DraftPartTypes,
    type RuleInsurance,
    type RuleMakes,
} from '../../../../components/settings/partRuleValues';
import type { AllowablePartType } from '../../../../hooks/useGetSettings';
import type { CatalogPositionGroup } from '../../../../hooks/useGetPCDBCatalog';

/** Which field the bulk editor is set to change. */
export type PartAllowanceBulkMode = 'position' | 'partTypes' | 'makes' | 'insurance' | 'move';

/** One value, applied to every selected rule. */
export type PartAllowanceBulkValue =
    | { mode: 'position'; groupName: string }
    | { mode: 'partTypes'; partTypes: AllowablePartType[] }
    | { mode: 'makes'; makes: RuleMakes }
    | { mode: 'insurance'; insurance: RuleInsurance }
    | { mode: 'move'; target: string };

export interface MoveTargetOption {
    id: string;
    label: string;
    /** One line inside the card, saying what the destination table does. */
    description: string;
    icon: ElementType;
}

export interface PartAllowanceBulkModalProps {
    mode: PartAllowanceBulkMode | null;
    selectedCount: number;
    /**
     * Position groups every selected part has in common. Positions are
     * part-specific, so a group only applies in bulk when all of them offer it.
     */
    sharedPositionGroups: CatalogPositionGroup[];
    oemOptions: Array<{ _id: string; name: string; }>;
    /** The other allowance tables a selection can be moved into. */
    moveTargets: MoveTargetOption[];
    onApply: (value: PartAllowanceBulkValue) => void;
    onClose: () => void;
}

const TITLES: Record<PartAllowanceBulkMode, string> = {
    position: 'Set position',
    partTypes: 'Set part types',
    makes: 'Set OEMs',
    insurance: 'Set prohibited insurance',
    move: 'Move parts',
};

/**
 * One value picked once and written to every selected rule, using the same
 * pickers the inline cells open. Like the cells, the change is staged — the
 * section's save bar is what commits it.
 */
const PartAllowanceBulkModal = ({
    mode,
    selectedCount,
    sharedPositionGroups,
    oemOptions,
    moveTargets,
    onApply,
    onClose,
}: PartAllowanceBulkModalProps) => {
    const [positionGroup, setPositionGroup] = useState<string>(ALL_POSITIONS);
    const [partTypes, setPartTypes] = useState<DraftPartTypes>({ ...defaultPartTypes });
    const [makes, setMakes] = useState<RuleMakes>('all');
    const [insurance, setInsurance] = useState<RuleInsurance>(undefined);
    const [moveTarget, setMoveTarget] = useState<string>('');

    // Reset between openings so a previous pick never carries over.
    useEffect(() => {
        if (!mode) return;
        setPositionGroup(ALL_POSITIONS);
        setPartTypes({ ...defaultPartTypes });
        setMakes('all');
        setInsurance(undefined);
        setMoveTarget(moveTargets[0]?.id ?? '');
    }, [mode, moveTargets]);

    if (!mode) return null;

    const handleApply = () => {
        switch (mode) {
            case 'position':
                onApply({ mode, groupName: positionGroup });
                break;
            case 'partTypes':
                onApply({ mode, partTypes: draftToAllowable(partTypes) });
                break;
            case 'makes':
                onApply({ mode, makes });
                break;
            case 'insurance':
                onApply({ mode, insurance });
                break;
            case 'move':
                onApply({ mode, target: moveTarget });
                break;
        }
    };

    const isApplyDisabled = mode === 'move' && moveTarget === '';

    return (
        <Modal isOpen onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader fontSize="md" fontWeight="semibold">
                    {TITLES[mode]}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={2}>
                    <VStack align="stretch" spacing={4}>
                        <Text fontSize="sm" color="gray.500">
                            {mode === 'move'
                                ? `Moves ${selectedCount} selected part${selectedCount === 1 ? '' : 's'} out of this table.`
                                : `Applies to ${selectedCount} selected part${selectedCount === 1 ? '' : 's'}.`}
                        </Text>

                        {mode === 'position' && (
                            <VStack align="stretch" spacing={3}>
                                <RadioGroup value={positionGroup} onChange={setPositionGroup}>
                                    <VStack align="stretch" spacing={2}>
                                        <Radio value={ALL_POSITIONS} size="sm" colorScheme="partbay-blue">
                                            <Text fontSize="sm" color="gray.700">{ALL_POSITIONS}</Text>
                                        </Radio>
                                        {sharedPositionGroups.map((group) => (
                                            <Radio
                                                key={group.groupName}
                                                value={group.groupName}
                                                size="sm"
                                                colorScheme="partbay-blue"
                                            >
                                                <Text fontSize="sm" color="gray.700">
                                                    {group.displayName || group.groupName}
                                                </Text>
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                                {sharedPositionGroups.length === 0 && (
                                    <Alert status="info" fontSize="sm" borderRadius="md">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            The selected parts share no position groups, so only
                                            All Positions can be set for all of them at once.
                                        </Text>
                                    </Alert>
                                )}
                            </VStack>
                        )}

                        {mode === 'partTypes' && (
                            <PartTypesPicker value={partTypes} onChange={setPartTypes} />
                        )}

                        {mode === 'makes' && (
                            <RuleMakesPicker
                                value={makes}
                                options={oemOptions}
                                onChange={setMakes}
                                maxH="260px"
                            />
                        )}

                        {mode === 'insurance' && (
                            <RuleInsurancePicker value={insurance} onChange={setInsurance} />
                        )}

                        {mode === 'move' && (
                            <SettingsChoiceCards
                                value={moveTarget}
                                onChange={setMoveTarget}
                                choices={moveTargets.map((target) => ({
                                    value: target.id,
                                    label: target.label,
                                    description: target.description,
                                    icon: target.icon,
                                }))}
                            />
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="sm" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <PartBayButton
                        size="sm"
                        variant="solid"
                        colorScheme="partbay-blue"
                        onClick={handleApply}
                        isDisabled={isApplyDisabled}
                        text={mode === 'move' ? 'Move' : 'Apply'}
                    />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PartAllowanceBulkModal;
